import { app } from "js/namespaces";
import React, { Component } from "react";
import { icons as iconsApi } from "apis/callables";
import { RealTimeSearchInput } from "js/react/components";
import { IconAsset } from "js/core/models/assets";
import Icon from "js/react/views/AddAssets/Panes/Icon";
import Spinner from "js/react/components/Spinner";
import { trackActivity } from "js/core/utilities/utilities";
import { UIContainerContext, UIPane, UIPaneContents, UIPaneHeader } from "js/react/components/UiComponents";
import { Fab, Icon as MuiIcon } from "@mui/material";
import { Gap10 } from "js/react/components/Gap";
import { SUGGEST_ICON_FORM } from "common/constants";
import { NoMatchNotice } from "js/react/components/Notice";
import { iconSearch, iconData, baseUrl } from "common/iconSearch";

export class IconsPane extends Component {
    static contextType = UIContainerContext;

    constructor(props) {
        super(props);
        this.searchRef = new React.createRef();
        this.paneRef = new React.createRef();

        this.state = {
            isSearching: false,
            searchInput: null,
            searchResults: null,
            iconKeys: null,
            visibleCount: 120,
            incrementSize: 120
        };

        this.handleSearchPromiseChain = Promise.resolve();
    }

    componentDidMount = async () => {
        const { orderedIconKeys } = await iconsApi.getUserRecommendations({ userId: app.user.id });

        const userRecommendedKeys = orderedIconKeys || [];
        const allIconKeys = Object.keys(iconData).reverse();

        const iconKeys = Array.from(new Set([
            ...userRecommendedKeys,
            ...allIconKeys
        ]));

        this.setState({
            iconKeys
        });
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                this.calculateInitialVisibleCount();
            });
        });
    };

    calculateInitialVisibleCount = () => {
        const container = document.querySelector(".search-results-container.icons");

        const containerComputedStyle = window.getComputedStyle(container);
        const gridTemplateColumns = containerComputedStyle.getPropertyValue("grid-template-columns");

        const columnsCount = gridTemplateColumns.split(" ").length;

        const scrollablePane = document.querySelector(".ui-inner-pane");
        const visibleHeight = scrollablePane.clientHeight;

        const rowsThatFit = Math.ceil(visibleHeight / 90) + 2;

        const optimalCount = columnsCount * rowsThatFit;

        if (optimalCount > 0 && optimalCount !== this.state.visibleCount) {
            const finalCount = Math.max(optimalCount, 40);

            this.setState({
                visibleCount: finalCount,
                incrementSize: finalCount
            });
        }
    };

    selectIcon = async iconKey => {
        const { addAssetCallback, handleConfirm } = this.props;
        trackActivity("Icon", "Selected", iconKey);
        addAssetCallback(new IconAsset({ id: iconKey }, { autoLoad: false }));

        const userId = app.user.id;
        await iconsApi.recordIconSelection({ userId, iconKey });

        handleConfirm();
    };

    renderAllIcons = () => {
        const { iconKeys, visibleCount } = this.state;
        const iconStyle = app.currentTheme?.get("iconStyle") ?? "chunky";

        return (
            <div className="search-results-wrapper icons">
                <div className="search-results-container icons">
                    {
                        (iconKeys || []).slice(0, visibleCount).map((iconKey, idx) => (
                            <Icon
                                icon={iconKey}
                                key={`${iconKey}-${idx}`}
                                style={iconStyle}
                                baseUrl={baseUrl}
                                onClick={() => this.selectIcon(iconKey)}
                            />
                        ))
                    }
                </div>
            </div>
        );
    };

    handleScroll = event => {
        const { iconKeys, visibleCount, incrementSize } = this.state;
        if (!iconKeys || visibleCount >= iconKeys.length) return;
        const target = event.target;
        const threshold = 200;
        const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
        if (distanceFromBottom <= threshold) {
            this.setState(({ visibleCount: current }) => ({
                visibleCount: Math.min(current + incrementSize, iconKeys.length)
            }));
        }
    };

    handleSearch = (input, results, query) => {
        this.handleSearchPromiseChain = this.handleSearchPromiseChain
            .then(() => new Promise(resolve => {
                this.setState({
                    searchInput: input,
                    searchResults: query ? results : null,
                    lastQuery: query,
                    isSearching: false
                }, resolve);
            }));
    };

    renderSearchResults = results => {
        if (!results || results.length === 0) {
            return (
                <NoMatchNotice
                    message={
                        <div>
                            <span>Try a different search term or </span>
                            <a
                                href={SUGGEST_ICON_FORM}
                                target="_blank"
                            >
                                suggest an icon.
                            </a>
                        </div>
                    }
                />
            );
        }

        const iconStyle = app.currentTheme?.get("iconStyle") ?? "chunky";
        return (
            <div className="search-results-wrapper icons">
                <div className="search-results-container icons">
                    {
                        results.map((result, idx) => {
                            const iconKey = iconData[result.ref].key;
                            return (
                                <Icon
                                    icon={iconKey}
                                    key={`${iconKey}-${idx}`}
                                    style={iconStyle}
                                    baseUrl={baseUrl}
                                    onClick={() => this.selectIcon(iconKey)}
                                />
                            );
                        })
                    }
                </div>
            </div>
        );
    };

    render() {
        const { searchInput, searchResults, isSearching } = this.state;
        const inputFocused = !this.context;

        return (
            <UIPane style={{ position: "relative" }}>
                <UIPaneHeader>
                    <RealTimeSearchInput
                        ref={this.searchRef}
                        searchInput={searchInput}
                        search={iconSearch}
                        focus={inputFocused}
                        onSearch={this.handleSearch}
                        placeholder="Search for icons..."
                    />
                </UIPaneHeader>
                {isSearching && <Spinner />}
                {!isSearching && <UIPaneContents ref={this.paneRef} onScroll={this.handleScroll} style={{ paddingBottom: 80, overflowY: "auto" }}>
                    {searchResults && this.renderSearchResults(searchResults)}
                    {!searchResults && this.renderAllIcons()}
                </UIPaneContents>}
                <Fab
                    variant="extended"
                    className="floating-fab expanding-fab"
                    onClick={() => window.open(SUGGEST_ICON_FORM, "_blank")}
                >
                    <MuiIcon className="text-blue">how_to_vote</MuiIcon>
                    <Gap10 />
                    <span>Suggest an icon</span>
                </Fab>
            </UIPane>
        );
    }
}

export default IconsPane;
